import type { AddCaseTemplateFormValues } from "@/components/Forms/SubForms/AddEditCaseTemplateDialog.tsx";
import { useFetchTaskTemplatesForAutocomplete } from "@/queries/useFetchTaskTemplatesForAutocomplete.ts";
import { Autocomplete, TextField, Typography } from "@mui/material";
import { type ChangeEvent, useMemo, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

type Option = {
    id: string;
    name: string;
};

type Props = {
    form: UseFormReturn<AddCaseTemplateFormValues>;
};

const TaskTemplatesAutocomplete = ({ form }: Props) => {
    const formValue = form.watch("taskTemplates");
    const [searchString, setSearchString] = useState("");
    const isQueryEnabled =
        searchString.length >= 2 || (formValue !== null && formValue !== undefined);
    const error = useMemo(() => {
        return form.formState.errors.taskTemplates;
    }, [form.formState.errors]);

    const {
        data: { data } = {},
        isError,
        isRefetching,
        isLoading,
    } = useFetchTaskTemplatesForAutocomplete({
        searchString: searchString,
        isQueryEnabled: isQueryEnabled,
        subQueryKey: "task-template-autocomplete",
    });

    const options: Option[] = useMemo(() => {
        if (data) {
            return data.map((company) => ({
                id: company.idUUID,
                name: company.displayName ?? "",
            }));
        }

        return [];
    }, [data]);

    return (
        <>
            {isError && <Typography variant={"h5"}>Error loading task templates.</Typography>}
            <Autocomplete
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Task template name"
                        value={searchString}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            if (event.target.value.length >= 2) {
                                setSearchString(event.target.value);
                            }
                        }}
                    />
                )}
                options={options}
                loading={isLoading || isRefetching}
                getOptionLabel={(option: Option) => option.name ?? ""}
                onChange={(_event, value) => {
                    if (formValue?.find((taskTemplate) => taskTemplate.id === value?.id)) {
                        return;
                    }

                    if (formValue && value) {
                        const newValues = [...formValue];
                        newValues.push(value);
                        form.setValue("taskTemplates", newValues);
                    }
                }}
                autoHighlight
                blurOnSelect
                sx={{
                    "& .MuiAutocomplete-inputRoot": {
                        padding: "4px!important",
                    },
                    "& .MuiAutocomplete-input": {
                        paddingTop: "4px!important",
                        paddingBottom: "4px!important",
                    },
                }}
            />
            <Typography sx={{ ml: 2, fontSize: "14px" }}>
                Start Typing Task Template Name
            </Typography>
            {error && (
                <Typography color="error" sx={{ mt: 1 }}>
                    {error.message}
                </Typography>
            )}
        </>
    );
};

export default TaskTemplatesAutocomplete;
